import React, { useState } from 'react';
import {
  CheckCircle,
  AlertTriangle,
  XCircle,
  Loader2,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
} from 'lucide-react';
import { useDqtRuns, DqtRunDto } from '../../api/hooks/useDataQuality';
import { formatDate, formatDateTime } from '../../utils/formatters';

interface DqtRunsTableProps {
  onRunSelect: (runId: string) => void;
  selectedRunId: string | null;
}

const PAGE_SIZE = 20;

const TEST_TYPE_LABELS: Record<string, string> = {
  IssuedInvoiceComparison: 'Porovnání faktur',
  ProductPairing: 'Párování produktů',
  StockWriteBackReconciliation: 'Zpětný zápis skladu',
  LotSumVsErpStock: 'Šarže vs. ERP sklad',
  PriceComparison: 'Kontrola cen',
};

const StatusIcon: React.FC<{ run: DqtRunDto }> = ({ run }) => {
  if (run.status === 'Failed') {
    return <XCircle className="h-4 w-4 text-red-500 dark:text-red-400" />;
  }
  if (run.status === 'Running') {
    return <Loader2 className="h-4 w-4 text-indigo-500 dark:text-graphite-accent animate-spin" />;
  }
  if (run.status === 'Completed' && (run.totalMismatches ?? 0) > 0) {
    return <AlertTriangle className="h-4 w-4 text-yellow-500" />;
  }
  return <CheckCircle className="h-4 w-4 text-green-500" />;
};

const DqtRunsTable: React.FC<DqtRunsTableProps> = ({ onRunSelect, selectedRunId }) => {
  const [pageNumber, setPageNumber] = useState(1);

  const { data, isLoading, error } = useDqtRuns({ pageNumber, pageSize: PAGE_SIZE });

  const items = data?.items ?? [];
  const totalPages = data?.totalPages ?? 1;
  const totalCount = data?.totalCount ?? 0;

  const handlePageChange = (newPage: number) => {
    if (newPage >= 1 && newPage <= totalPages) {
      setPageNumber(newPage);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="flex items-center gap-2 text-gray-500 dark:text-graphite-muted">
          <Loader2 className="h-5 w-5 animate-spin text-indigo-500 dark:text-graphite-accent" />
          Načítání testů...
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-40">
        <div className="flex items-center gap-2 text-red-600 dark:text-red-400">
          <AlertCircle className="h-5 w-5" />
          Chyba při načítání: {(error as Error).message}
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-graphite-border">
          <thead className="bg-gray-50 dark:bg-graphite-surface-2 sticky top-0 z-10">
            <tr>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase tracking-wider w-8" />
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase tracking-wider">
                Typ testu
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase tracking-wider">
                Období
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase tracking-wider">
                Zkontrolováno
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase tracking-wider">
                Neshody
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase tracking-wider">
                Spuštění
              </th>
              <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 dark:text-graphite-muted uppercase tracking-wider">
                Čas
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-graphite-surface divide-y divide-gray-200 dark:divide-graphite-border">
            {items.map((run) => {
              const isSelected = run.id === selectedRunId;
              return (
                <tr
                  key={run.id}
                  onClick={() => onRunSelect(run.id ?? '')}
                  className={`cursor-pointer transition-colors ${
                    isSelected
                      ? 'bg-indigo-50 dark:bg-graphite-accent/10 border-l-2 border-indigo-500 dark:border-graphite-accent'
                      : 'hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                >
                  <td className="px-3 py-3">
                    <StatusIcon run={run} />
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-700 dark:text-graphite-muted whitespace-nowrap">
                    {run.testType ? (TEST_TYPE_LABELS[run.testType] ?? run.testType) : '—'}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-900 dark:text-graphite-text whitespace-nowrap">
                    {formatDate(run.dateFrom)} — {formatDate(run.dateTo)}
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-700 dark:text-graphite-muted text-center">
                    {run.totalChecked}
                  </td>
                  <td className="px-3 py-3 text-sm font-medium text-center">
                    <span
                      className={(run.totalMismatches ?? 0) > 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-emerald-400'}
                    >
                      {run.totalMismatches}
                    </span>
                  </td>
                  <td className="px-3 py-3">
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                        run.triggerType === 'Manual'
                          ? 'bg-indigo-100 text-indigo-800 dark:bg-graphite-accent/10 dark:text-graphite-accent'
                          : 'bg-gray-100 text-gray-700 dark:bg-graphite-surface-2 dark:text-graphite-muted'
                      }`}
                    >
                      {run.triggerType === 'Manual' ? 'Ruční' : 'Plánované'}
                    </span>
                  </td>
                  <td className="px-3 py-3 text-sm text-gray-500 dark:text-graphite-muted whitespace-nowrap">
                    {formatDateTime(run.startedAt)}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {items.length === 0 && (
          <div className="text-center py-8 text-gray-500 dark:text-graphite-muted text-sm">Žádné testy nebyly nalezeny.</div>
        )}
      </div>

      {/* Pagination */}
      {totalCount > 0 && (
        <div className="flex-shrink-0 bg-white dark:bg-graphite-surface border-t border-gray-200 dark:border-graphite-border px-3 py-2 flex items-center justify-between text-xs">
          <p className="text-gray-600 dark:text-graphite-muted">
            {Math.min((pageNumber - 1) * PAGE_SIZE + 1, totalCount)}–
            {Math.min(pageNumber * PAGE_SIZE, totalCount)} z {totalCount}
          </p>
          <nav className="inline-flex rounded shadow-sm -space-x-px">
            <button
              onClick={() => handlePageChange(pageNumber - 1)}
              disabled={pageNumber <= 1}
              className="inline-flex items-center px-2 py-1 rounded-l border border-gray-300 dark:border-graphite-border bg-white dark:bg-graphite-surface text-xs font-medium text-gray-500 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="h-3 w-3" />
            </button>
            {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
              let pageNum: number;
              if (totalPages <= 5) {
                pageNum = i + 1;
              } else if (pageNumber <= 3) {
                pageNum = i + 1;
              } else if (pageNumber >= totalPages - 2) {
                pageNum = totalPages - 4 + i;
              } else {
                pageNum = pageNumber - 2 + i;
              }
              return (
                <button
                  key={pageNum}
                  onClick={() => handlePageChange(pageNum)}
                  className={`inline-flex items-center px-2 py-1 border text-xs font-medium ${
                    pageNum === pageNumber
                      ? 'z-10 bg-indigo-50 dark:bg-graphite-accent/10 border-indigo-500 dark:border-graphite-accent text-indigo-600 dark:text-graphite-accent'
                      : 'bg-white dark:bg-graphite-surface border-gray-300 dark:border-graphite-border text-gray-500 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
            <button
              onClick={() => handlePageChange(pageNumber + 1)}
              disabled={pageNumber >= totalPages}
              className="inline-flex items-center px-2 py-1 rounded-r border border-gray-300 dark:border-graphite-border bg-white dark:bg-graphite-surface text-xs font-medium text-gray-500 dark:text-graphite-muted hover:bg-gray-50 dark:hover:bg-white/5 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="h-3 w-3" />
            </button>
          </nav>
        </div>
      )}
    </div>
  );
};

export default DqtRunsTable;
